import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, MeResponse } from '../api/client';

const StatCard: React.FC<{ icon: string; label: string; value: string | number; color?: string }> = ({ icon, label, value, color = 'text-green-400' }) => (
  <div className="p-4 rounded-xl bg-gray-900/70 border border-gray-800">
    <div className="flex items-center gap-2">
      <i className={`${icon} ${color}`}></i>
      <span className="text-sm text-gray-400">{label}</span>
    </div>
    <div className="mt-2 text-2xl font-semibold">{value}</div>
  </div>
);

const ActionCard: React.FC<{ to: string; icon: string; title: string; desc: string; color?: string }> = ({ to, icon, title, desc, color = 'text-indigo-400' }) => (
  <Link to={to} className="p-5 rounded-xl bg-gray-900/70 border border-gray-800 hover:border-indigo-500 transition">
    <div className="flex items-center gap-3">
      <i className={`${icon} ${color}`}></i>
      <div>
        <div className="font-semibold">{title}</div>
        <div className="text-sm text-gray-400">{desc}</div>
      </div>
    </div>
  </Link>
);

const Dashboard: React.FC = () => {
  const { user, token } = useAuth();
  const [quests, setQuests] = useState<any[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [me, setMe] = useState<MeResponse['user'] | null>(null);

  useEffect(() => {
    if (!token) return;
    api.get<{ ok: boolean; quests: any[] }>("/quests", token).then((res) => setQuests(res.quests || [])).catch(() => setQuests([]));
    api.get<MeResponse>("/auth/me", token).then((res) => setMe(res.user)).catch(() => setMe(null));
  }, [token]);

  const claimQuest = async (id: string) => {
    if (!token) return;
    setMsg(null);
    try {
      await api.post(`/quests/${id}/claim`, undefined, token);
      const q = quests.map((qq) => (qq._id === id ? { ...qq, status: 'completed' } : qq));
      setQuests(q);
      const refreshed = await api.get<MeResponse>("/auth/me", token);
      setMe(refreshed.user);
      setMsg('Quest claimed and rewards applied.');
    } catch (err: any) {
      setMsg(err?.message || 'Claim failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="p-6 rounded-2xl bg-gradient-to-r from-gray-900 via-gray-950 to-black border border-gray-800">
        <div className="flex items-center gap-3">
          <i className="fas fa-seedling text-green-400 text-xl"></i>
          <h1 className="text-2xl font-bold">Welcome{user?.username ? `, ${user.username}` : ''}</h1>
        </div>
        <p className="mt-2 text-sm text-gray-400">Manage plots, trade produce, climb the leaderboards, and complete quests. Your sustainable farm journey starts here.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon="fas fa-leaf" label="Sustainability Score" value={user?.sustainabilityScore ?? '-'} />
        <StatCard icon="fas fa-tractor" label="Active Plots" value={'—'} color="text-amber-400" />
        <StatCard icon="fas fa-store" label="Recent Orders" value={'—'} color="text-sky-400" />
        <StatCard icon="fas fa-trophy" label="Leaderboard Rank" value={'—'} color="text-yellow-400" />
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <ActionCard to="/plots" icon="fas fa-tractor" title="Manage Plots" desc="Irrigate, plant, fertilize, scout, and get satellite reports." color="text-emerald-400" />
        <ActionCard to="/market" icon="fas fa-store" title="Trade on Market" desc="View live ticker and create tailored orders." color="text-sky-400" />
        <ActionCard to="/leaderboards" icon="fas fa-trophy" title="Leaderboards" desc="Compete by season and track your rank." color="text-yellow-400" />
        <ActionCard to="/quests" icon="fas fa-scroll" title="Quests" desc="Claim quest rewards and progress milestones." color="text-pink-400" />
      </div>

      {/* Quests */}
      <div className="p-5 rounded-xl bg-gray-900/70 border border-gray-800">
        <h2 className="text-lg font-semibold flex items-center gap-2"><i className="fas fa-scroll text-pink-400"></i> <span>Active Quests</span></h2>
        {msg && <div className="mt-2 text-sm text-gray-300">{msg}</div>}
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {quests.length === 0 && <div className="text-sm text-gray-400">No quests yet.</div>}
          {quests.map((q) => (
            <div key={q._id} className="p-4 rounded-lg bg-gray-800/60 border border-gray-700">
              <div className="font-medium">{q.title}</div>
              <div className="text-xs text-gray-400">Status: {q.status}</div>
              <div className="mt-2 flex items-center gap-2">
                <button disabled={q.status === 'completed'} onClick={() => claimQuest(q._id)} className={`px-3 py-1 rounded ${q.status === 'completed' ? 'bg-gray-700 cursor-not-allowed' : 'bg-green-600 hover:bg-green-500'}`}>
                  {q.status === 'completed' ? 'Claimed' : 'Claim'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Inventory & Achievements */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="p-5 rounded-xl bg-gray-900/70 border border-gray-800">
          <h2 className="text-lg font-semibold flex items-center gap-2"><i className="fas fa-boxes text-sky-400"></i> <span>Inventory</span></h2>
          <div className="mt-3 space-y-2">
            {(me?.inventory || user?.inventory || []).length === 0 && <div className="text-sm text-gray-400">No items yet.</div>}
            {(me?.inventory || user?.inventory || []).map((it) => (
              <div key={it.itemId} className="flex items-center justify-between p-3 rounded bg-gray-800/60 border border-gray-700">
                <span className="text-sm">{it.name}</span>
                <span className="text-sm text-gray-300">x{it.qty}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="p-5 rounded-xl bg-gray-900/70 border border-gray-800">
          <h2 className="text-lg font-semibold flex items-center gap-2"><i className="fas fa-medal text-yellow-400"></i> <span>Achievements</span></h2>
          <div className="mt-3 space-y-2">
            {(me?.achievements || user?.achievements || []).length === 0 && <div className="text-sm text-gray-400">No achievements yet.</div>}
            {(me?.achievements || user?.achievements || []).map((a) => (
              <div key={a.key} className="flex items-center justify-between p-3 rounded bg-gray-800/60 border border-gray-700">
                <span className="text-sm">{a.key}</span>
                <span className="text-xs text-gray-400">{new Date(a.earnedAt).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;